import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import {
    TRAINSIM_CONFIG,
    DIR_DELTA, OPPOSITE, getExitDir, isDiagonalDir,
} from './trainsimConfig.js';
import { computeBrakeTargetFactor, easeSpeedFactor } from './TrainSimSignals.js';
import { drawTramCarRect } from '../../ui/TramCar.js';

/**
 * Orient a vehicle sprite so its TOP always faces toward the top of
 * the screen — never upside-down. Eastward motion renders normally;
 * westward motion is achieved by mirroring horizontally (scale.x =
 * -1) instead of rotating 180°, which would otherwise flip the
 * artwork onto its roof. Vertical/diagonal motion still rotates,
 * but is constrained to the upper half-plane so the roof stays up.
 *
 * Mathematically: pick (scale, rot) so that the rendered sprite's
 * "front" still points along `angle` AND its "top" has cos ≥ 0
 * (i.e. points anywhere from west-up to east-up, never down).
 */
function setUprightRotation(disp, angle) {
    if (Math.cos(angle) >= 0) {
        disp.rotation = angle;
        if (disp.scale.x < 0) disp.scale.x = -disp.scale.x; // restore +1
    } else {
        disp.rotation = angle - Math.PI;
        if (disp.scale.x > 0) disp.scale.x = -disp.scale.x; // flip to -1
    }
}

/**
 * Auto-moving train for Train Simulator.
 *
 * Adapted from PipelineTrain — same Catmull-Rom smooth follower with a
 * head + body car, but extended to:
 *
 *   - START DELAY: stays invisible until its `startDelay` seconds elapse.
 *   - LAMP-AWARE: before stepping into the next cell, the scene tells us
 *     if that cell holds a red lamp; if so, `paused` is set and we hold
 *     position at the current cell centre.
 *   - SWITCH-AWARE: the scene injects each cell's "live" connections
 *     (incorporating the current switch state) when we need to pick an
 *     exit direction. The train itself doesn't know about the switch
 *     entities — it just trusts the connections it gets handed.
 *   - PER-TRAIN COLOUR: head + body Graphics get a tint matching the
 *     train's id ('t1'..'t5') so all 5 trams read distinct on screen.
 *   - 8-WAY GRID: directions and the connection arrays it consumes
 *     are 8-way (N, NE, E, SE, S, SW, W, NW) so trains can traverse
 *     45° diagonal track. Diagonal cells span √2 × cellSize on screen,
 *     so the per-cell progress increment is scaled by 1/√2 to keep
 *     constant px/sec.
 */
export class TrainSimTrain {
    /** @type {Container} root container; same parent layer as track tiles */
    gfx = new Container();

    // ── grid state ─────────────────────────────────────
    _col = 0;            // current cell (entered)
    _row = 0;
    _prevCol = 0;
    _prevRow = 0;
    _nextCol = 0;
    _nextRow = 0;
    _lookaheadCol = 0;
    _lookaheadRow = 0;
    _entryDir = 3;       // dir we ENTERED current cell from (a DIR_DELTA index)
    _exitDir  = 1;       // dir we'll LEAVE current cell from
    _progress = 0;       // 0..1 fraction across current cell

    _started = false;    // false until startDelay has elapsed
    _waitTimer = 0;      // accumulated seconds since spawn (vs startDelay)
    _announcedDeparture = false;  // bell+flash fired once 10 s before start
    _isBoarding = false;          // true during the [-10s, 0] pre-departure window
    _isCountingDown = false;      // true during the final [-4s, 0] beep countdown
    _countdownPlayed = false;     // countdown.mp3 fired once at the -4 s mark
    _alive = true;       // false after crash; scene will tear us down
    _reachedGoal = false;
    /** When `_alive` is false, holds 'crash' | 'wrong-dest' so the
     *  scene can pick the right user-facing warning message. */
    _crashReason = null;
    paused = false;      // true when blocked by a red switch signal at the next cell

    _speed = TRAINSIM_CONFIG.TRAIN_BASE_SPEED;
    // Dynamic cruise/brake multiplier on top of `_speed` (and the
    // manual TEMPÓ). Starts at cruise; eased toward a target each
    // frame so the train decelerates smoothly approaching a red
    // switch signal and accelerates back up once it's green.
    _speedFactor = TRAINSIM_CONFIG.TRAIN_CRUISE_FACTOR;
    _cellSize = 0;
    _color = 0xffffff;

    // ── identity ───────────────────────────────────────
    trainId = 't1';
    spawnCol = 0;
    spawnRow = 0;
    spawnDir = 1;
    startDelay = 0;
    goalCol = -1;
    goalRow = -1;
    destinationCity = '';

    // ── visuals ────────────────────────────────────────
    _headGfx = new Graphics();
    _tailGfx = new Graphics();
    _destLabel = null;            // floating chip "→ <city>" above the head
    _arrivalFadeTimer = 0;        // seconds since reachedGoal fired
    _trail = [];
    // Last good articulated angles (radians). Kept so a frame where
    // the head/joint/tail samples momentarily coincide (e.g. the
    // first frame after spawn, before the trail has length) reuses a
    // sane heading instead of snapping to atan2(0,0)=0 (due east).
    _lastHeadFwd = 0;
    _lastTailFwd = 0;
    _themeManager = null;

    /**
     * @param {object} opts
     * @param {string} opts.trainId
     * @param {number} opts.spawnCol
     * @param {number} opts.spawnRow
     * @param {number} opts.spawnDir
     * @param {number} opts.startDelay seconds
     * @param {number} opts.goalCol
     * @param {number} opts.goalRow
     * @param {string} [opts.destinationCity] human-readable destination city; drawn as a chip above the head wagon so the player can identify each train by where it's heading even when several are in motion.
     * @param {number} opts.cellSize
     * @param {number} opts.color
     * @param {object} [opts.themeManager]
     */
    constructor(opts) {
        this.trainId = opts.trainId;
        this.spawnCol = opts.spawnCol;
        this.spawnRow = opts.spawnRow;
        this.spawnDir = opts.spawnDir;
        this.startDelay = opts.startDelay;
        this.goalCol = opts.goalCol;
        this.goalRow = opts.goalRow;
        this._cellSize = opts.cellSize;
        this._color = opts.color;
        this._themeManager = opts.themeManager || null;
        this.destinationCity = (opts.destinationCity || '').trim();

        this._col = this.spawnCol;
        this._row = this.spawnRow;
        this._prevCol = this.spawnCol - DIR_DELTA[this.spawnDir].dc;
        this._prevRow = this.spawnRow - DIR_DELTA[this.spawnDir].dr;
        this._nextCol = this.spawnCol + DIR_DELTA[this.spawnDir].dc;
        this._nextRow = this.spawnRow + DIR_DELTA[this.spawnDir].dr;
        this._lookaheadCol = this._nextCol + DIR_DELTA[this.spawnDir].dc;
        this._lookaheadRow = this._nextRow + DIR_DELTA[this.spawnDir].dr;
        // entryDir on spawn = the direction the train "came from" (opposite of spawnDir)
        this._entryDir = OPPOSITE[this.spawnDir];
        this._exitDir  = this.spawnDir;

        this._drawTramCar(this._headGfx, 'trainsim.tram.head');
        this._drawTramCar(this._tailGfx, 'trainsim.tram.body');
        // The head car draws ON TOP of the tail (higher zIndex) so the
        // tail's slightly-overlapping front edge tucks neatly under it
        // — a clean continuous body with no coupling glyph or seam.
        this._headGfx.zIndex = 2;
        this._tailGfx.zIndex = 1;
        this.gfx.addChild(this._tailGfx);
        this.gfx.addChild(this._headGfx);

        // Destination chip: a small "→ city" tag floating above the
        // head wagon so the player can identify each running train
        // by its goal even when several are in motion. The chip is
        // a child of the train's root container (not the rotated
        // head sprite), so it stays upright regardless of which way
        // the loco is pointing — same trick the game uses for car
        // sprites' upright orientation logic.
        if (this.destinationCity) {
            this._destLabel = this._buildDestChip(this.destinationCity);
            this._destLabel.zIndex = 3;
            this.gfx.addChild(this._destLabel);
        }

        this.gfx.sortableChildren = true;
        this.gfx.zIndex = 100;
        this.gfx.visible = false; // becomes visible after startDelay

        // Park the container so the first frame of visibility is at the
        // spawn cell's centre, not (0,0).
        const cs = this._cellSize;
        this.gfx.x = this._col * cs + cs / 2;
        this.gfx.y = this._row * cs + cs / 2;
    }

    /**
     * Called by the scene every frame.
     * The scene supplies a `boardApi` that abstracts the live cell state
     * including switch overrides:
     *
     *   boardApi.getConnections(col, row) -> 8-element [N,NE,E,SE,S,SW,W,NW] | null
     *   boardApi.isInside(col, row)       -> bool
     *   boardApi.isLampRed(col, row)      -> bool
     *   boardApi.isGoalFor(col, row, trainId) -> bool
     *
     * @param {number} delta frame delta (1 = 60fps)
     * @param {object} boardApi
     * @returns {{ alive: boolean, reachedGoal: boolean }}
     */
    update(delta, boardApi) {
        if (!this._alive) return { alive: false, reachedGoal: this._reachedGoal };
        if (this._reachedGoal) {
            // Arrival fade-out: the scene has already kicked off the
            // confetti + banner; here we drift the whole train (head
            // + tail + coupler + dest chip) from full alpha to zero
            // over `fadeDur` so the loco visibly disappears at its
            // destination instead of parking forever on the goal
            // cell. Once fully invisible, we hide the gfx so further
            // ticks are a cheap no-op.
            const dt = (delta / 60);
            this._arrivalFadeTimer += dt;
            const fadeDur = 1.5;
            if (this._arrivalFadeTimer < fadeDur) {
                this.gfx.alpha = Math.max(0, 1 - this._arrivalFadeTimer / fadeDur);
            } else if (this.gfx.visible) {
                this.gfx.alpha = 0;
                this.gfx.visible = false;
            }
            return { alive: true, reachedGoal: true };
        }

        const dtSec = delta / 60;

        // ── start-delay countdown ─────────────────────────
        if (!this._started) {
            this._waitTimer += dtSec;
            // Boarding window: the final 10 seconds before the
            // train pulls out of the station. The scene's announcer
            // tick checks `_isBoarding` and fires the departure
            // bell + flashing menetrend row so the player gets a
            // visible/audible heads-up that THIS row is next. Flag
            // alone has no gameplay effect; it's purely a comms
            // hook for HUD + audio.
            this._isBoarding = (this.startDelay - this._waitTimer) <= 10;
            // Final beep countdown — the last 4 seconds (4·3·2·1)
            // before the train pulls out. The scene fires
            // `countdown.mp3` once when this flips and the board's
            // boarding FX shows the matching 4·3·2·1 number.
            this._isCountingDown = (this.startDelay - this._waitTimer) <= 4;
            if (this._waitTimer < this.startDelay) {
                return { alive: true, reachedGoal: false };
            }
            // Time to enter the world.
            const ok = this._beginRun(boardApi);
            if (!ok) {
                this._alive = false;
                return { alive: false, reachedGoal: false };
            }
            this._started = true;
            this._isBoarding = false;
            this._isCountingDown = false;
            this.gfx.visible = true;
            // Park position is the spawn-cell centre; trail seed = same.
            const seed = this._sampleSegment(0);
            this._trail = [seed];
        }

        // ── dynamic cruise / brake ───────────────────────────
        // Ease the speed factor toward its target every frame: cruise
        // on open track, brake toward the slow floor when the switch
        // signal ahead is red. This decelerates the train smoothly
        // BEFORE it reaches the red switch and accelerates it back up
        // once the player flips the signal green.
        const brakeTarget = computeBrakeTargetFactor(boardApi, this);
        this._speedFactor = easeSpeedFactor(this._speedFactor, brakeTarget, dtSec);

        // ── pause-when-next-tile-is-a-red-switch-signal ───────
        // Don't roll into a switch whose signal is red — freeze at the
        // entry side of the current cell. Because the check keys off
        // the NEXT cell being the switch, it stops the train before the
        // switch from ANY approach direction. Once the player flips the
        // signal green we resume on the next frame.
        this.paused = boardApi.isSwitchSignalRed
            ? boardApi.isSwitchSignalRed(this._nextCol, this._nextRow)
            : false;
        if (this.paused) return { alive: true, reachedGoal: false };

        // ── advance fractional progress through current cell ─
        //
        // Diagonal segments cover √2 × cellSize on screen vs. 1 × cellSize
        // for cardinal segments, so to keep visual speed (px/sec) constant
        // we slow the fractional progress on diagonals by 1/√2. Cardinal
        // cells advance at the configured TRAIN_BASE_SPEED unchanged. The
        // cruise/brake factor multiplies on top of the manual TEMPÓ that
        // is already baked into `dtSec`.
        const cellLengthFactor = (isDiagonalDir(this._entryDir) || isDiagonalDir(this._exitDir))
            ? Math.SQRT2
            : 1;
        this._progress += (this._speed * this._speedFactor * dtSec) / cellLengthFactor;

        if (this._progress < 1) return { alive: true, reachedGoal: false };

        // ── last-mile collision guard ────────────────────────
        // The instant we're about to physically step into the next
        // cell, ask the board if anyone else is already sitting
        // there. If so, clamp progress to just-shy-of-1 and bail
        // out — we'll re-check next frame. Without this guard two
        // trains could end up on the same cell on the same tick
        // and both be reported as crashed simultaneously.
        if (typeof boardApi.isCellOccupiedByOther === 'function'
            && boardApi.isCellOccupiedByOther(this._nextCol, this._nextRow, this.trainId)) {
            this._progress = 0.999;
            return { alive: true, reachedGoal: false };
        }

        // ── step into the next cell ───────────────────────
        this._progress -= 1;
        this._col = this._nextCol;
        this._row = this._nextRow;

        if (!boardApi.isInside(this._col, this._row)) {
            return this._crash();
        }

        // Reached our own goal? Stop and report success.
        if (boardApi.isGoalFor(this._col, this._row, this.trainId)) {
            this._reachedGoal = true;
            return { alive: true, reachedGoal: true };
        }

        // Landed on ANOTHER train's goal cell? That's a "wrong
        // destination" event — surface it as a crash but tag the
        // reason so the scene can show the dedicated "rossz
        // célállomás" warning instead of the generic crash one.
        if (boardApi.isAnyGoalCell && boardApi.isAnyGoalCell(this._col, this._row)) {
            return this._crash('wrong-dest');
        }

        // Need a tile here to continue; if no tile at all → crash.
        const conns = boardApi.getConnections(this._col, this._row);
        if (!conns) return this._crash();

        this._entryDir = OPPOSITE[this._exitDir];
        if (!conns[this._entryDir]) return this._crash();

        // If a switch lives here, it ROUTES the train — it picks the
        // exit directly based on its current routing index (0..N-1
        // for the junction's supported pairings). Otherwise default
        // to the geometry-only `getExitDir` (straightest open exit).
        let exitDir = -1;
        if (boardApi.getSwitchExit) {
            exitDir = boardApi.getSwitchExit(this._col, this._row, this._entryDir);
        }
        if (exitDir === -1) exitDir = getExitDir(conns, this._entryDir);
        if (exitDir === -1) return this._crash();

        this._exitDir = exitDir;
        this._setPathState(boardApi, this._col, this._row, this._entryDir, exitDir);
        return { alive: true, reachedGoal: false };
    }

    /** Update visual position. Called every frame after update(). */
    render() {
        const cs = this._cellSize;

        if (!this._started) {
            // Stay parked at spawn while waiting for our start delay.
            this.gfx.x = this._col * cs + cs / 2;
            this.gfx.y = this._row * cs + cs / 2;
            return;
        }

        // Reached-goal: stop interpolating, sit on the goal cell centre.
        if (this._reachedGoal) {
            const last = this._sampleSegment(1);
            this._headGfx.x = last.x;
            this._headGfx.y = last.y;
            this.gfx.x = 0;
            this.gfx.y = 0;
            return;
        }

        // Mid-run: car positions are in board-local coords. Parent
        // container sits at (0,0).
        this.gfx.x = 0;
        this.gfx.y = 0;

        const headPos = this._sampleSegment(this._progress);
        this._pushTrailPoint(headPos);

        // ── Articulated two-car hinge ─────────────────────
        // The old model placed each car at its own centre and rotated
        // it about that centre, then stretched a coupling between the
        // head's rear and the tail's front. On curves those two edges
        // (computed by straight-line projection from each centre) land
        // on different points — chord vs. arc — so the coupling
        // visibly stretched: the "elastic bend".
        //
        // Instead we hinge BOTH cars on ONE shared joint point taken
        // straight off the trail (so it follows the rail curve):
        //   • HEAD rotates about its REAR — its rear edge is pinned to
        //     the joint and the body projects FORWARD toward the lead.
        //   • TAIL rotates about its FRONT — its front edge is pinned
        //     to the same joint and the body projects BACKWARD.
        // Because both edges sit on the identical joint point there is
        // no gap to stretch; the cars simply pivot around the hinge
        // like a real articulated tram (Jacobs bogie).
        const carHalf = this._cellSize * 1.15;   // half of the 2.3-cell car
        const carLen  = this._cellSize * 2.30;    // full car length

        // Joint: one half-car behind the lead point, measured ALONG
        // the trail so it rides the curve. On straight track this puts
        // the head centre exactly where it used to be (carHalf forward
        // of a point carHalf back = the lead point), so nothing
        // changes on straights — only curves now hinge instead of
        // stretch.
        const joint   = this._sampleTrail(carHalf);
        // Reference point a further car-length back, used to orient the
        // tail (its body trails from the joint toward here).
        const tailRef = this._sampleTrail(carHalf + carLen);

        // HEAD forward = joint → lead. Reuse the last good angle if the
        // two points momentarily coincide (degenerate atan2).
        let headFwd = this._lastHeadFwd;
        {
            const dx = headPos.x - joint.x;
            const dy = headPos.y - joint.y;
            if ((dx * dx + dy * dy) > 0.01) {
                headFwd = Math.atan2(dy, dx);
                this._lastHeadFwd = headFwd;
            }
        }
        // TAIL forward = tailRef → joint (points the same way the tail
        // travels, i.e. toward the hinge).
        let tailFwd = this._lastTailFwd;
        {
            const dx = joint.x - tailRef.x;
            const dy = joint.y - tailRef.y;
            if ((dx * dx + dy * dy) > 0.01) {
                tailFwd = Math.atan2(dy, dx);
                this._lastTailFwd = tailFwd;
            }
        }

        // Car centres derived from the hinge so the pinned edges land
        // exactly on `joint`:
        //   head centre = joint + forward·carHalf  → head rear == joint
        //   tail centre = joint − forward·carHalf  → tail front == joint
        // Head's rear edge pins to the joint; its front reaches the
        // lead position. The tail front is tucked slightly PAST the
        // joint (a small overlap) so it slides under the head car —
        // which draws on top (higher zIndex) — leaving a clean,
        // continuous body with no visible seam and no coupling glyph.
        const coupleOverlap = this._cellSize * 0.16;
        const headCx = joint.x + Math.cos(headFwd) * carHalf;
        const headCy = joint.y + Math.sin(headFwd) * carHalf;
        const tailCx = joint.x - Math.cos(tailFwd) * (carHalf - coupleOverlap);
        const tailCy = joint.y - Math.sin(tailFwd) * (carHalf - coupleOverlap);

        this._headGfx.x = headCx;
        this._headGfx.y = headCy;
        setUprightRotation(
            this._headGfx,
            headFwd,
        );

        this._tailGfx.x = tailCx;
        this._tailGfx.y = tailCy;
        setUprightRotation(
            this._tailGfx,
            tailFwd,
        );

        // Destination chip floats one cell above the head sprite in
        // world-space (no rotation) so it's always readable. We
        // anchor on the head's pixel position so the chip slides
        // smoothly with the train rather than snapping cell-to-cell.
        if (this._destLabel) {
            this._destLabel.x = headPos.x;
            this._destLabel.y = headPos.y - this._cellSize * 1.05;
        }
    }

    /** Current head cell — used by the scene for crash detection. */
    get headCell() {
        return { c: this._col, r: this._row };
    }

    /**
     * Capture every mutable runtime field needed to faithfully
     * restore this train to its current state on a later
     * `restore(snap)` call. Used by the scene's REWIND feature to
     * undo the last few seconds of play.
     *
     * NOT a deep clone — we deliberately snapshot only PRIMITIVE
     * gameplay state (cell coords, direction enums, lifecycle
     * flags) plus a shallow copy of the trail buffer. Sprites
     * (Graphics / Container) re-render themselves from those
     * primitives on the next `render()` call, so we don't have
     * to roll back any GPU state.
     */
    snapshot() {
        return {
            col: this._col,
            row: this._row,
            prevCol: this._prevCol,
            prevRow: this._prevRow,
            nextCol: this._nextCol,
            nextRow: this._nextRow,
            lookaheadCol: this._lookaheadCol,
            lookaheadRow: this._lookaheadRow,
            entryDir: this._entryDir,
            exitDir:  this._exitDir,
            progress: this._progress,
            speedFactor: this._speedFactor,
            started:  this._started,
            waitTimer: this._waitTimer,
            alive: this._alive,
            reachedGoal: this._reachedGoal,
            crashReason: this._crashReason,
            paused: this.paused,
            // Trail = the last ~N head positions used to anchor the
            // tail wagon. Plain {x, y} entries so a shallow map clone
            // is enough to fully decouple from the live buffer.
            trail: this._trail.map((p) => ({ x: p.x, y: p.y })),
        };
    }

    /**
     * Apply a previously-captured `snapshot()` payload. Idempotent
     * if called repeatedly with the same payload. After restore the
     * very next `render()` will reposition every sprite back to the
     * snapshot's cell + progress, so callers don't need to clear
     * any visual state themselves.
     */
    restore(snap) {
        if (!snap) return;
        this._col = snap.col;
        this._row = snap.row;
        this._prevCol = snap.prevCol;
        this._prevRow = snap.prevRow;
        this._nextCol = snap.nextCol;
        this._nextRow = snap.nextRow;
        this._lookaheadCol = snap.lookaheadCol;
        this._lookaheadRow = snap.lookaheadRow;
        this._entryDir = snap.entryDir;
        this._exitDir  = snap.exitDir;
        this._progress = snap.progress;
        this._speedFactor = typeof snap.speedFactor === 'number'
            ? snap.speedFactor
            : TRAINSIM_CONFIG.TRAIN_CRUISE_FACTOR;
        this._started  = !!snap.started;
        this._waitTimer = snap.waitTimer;
        this._alive    = !!snap.alive;
        this._reachedGoal = !!snap.reachedGoal;
        this._crashReason = snap.crashReason ?? null;
        this.paused    = !!snap.paused;
        this._trail    = Array.isArray(snap.trail)
            ? snap.trail.map((p) => ({ x: p.x, y: p.y }))
            : [];
        // Trains pre-start are invisible; rewinding back to that
        // state must hide the sprite again so we don't leave a
        // ghost engine parked at the spawn cell.
        // Rewinds also need to undo the arrival fade-out: restore
        // full alpha + visibility so a train rewound out of its
        // "arrived" state pops back onto the map cleanly. The fade
        // timer resets only when the rewind restores a non-arrived
        // state — staying inside the arrival window keeps the fade
        // continuous if the player rewinds right at the goal.
        this.gfx.alpha = 1;
        this.gfx.visible = this._started;
        if (!this._reachedGoal) this._arrivalFadeTimer = 0;
        // Boarding flags aren't snapshotted (they're recomputed
        // each tick from waitTimer + startDelay), but the bell
        // should re-arm on rewind so a scrub back BEFORE the
        // 10-s window plays the bell again when the player crosses
        // the boundary a second time. Without this the flag would
        // stay "true" for the rest of the run.
        if (!this._started) {
            this._isBoarding = (this.startDelay - this._waitTimer) <= 10;
            if (!this._isBoarding) this._announcedDeparture = false;
            this._isCountingDown = (this.startDelay - this._waitTimer) <= 4;
            if (!this._isCountingDown) this._countdownPlayed = false;
        } else {
            this._isBoarding = false;
            this._isCountingDown = false;
        }
    }

    destroy() {
        if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
        this.gfx.destroy({ children: true });
    }

    // ── internals ──────────────────────────────────────

    _beginRun(boardApi) {
        // The train spawns sitting on its spawn cell, oriented along
        // spawnDir. The spawn cell itself must be a valid track cell
        // (otherwise it's an authoring mistake → crash immediately).
        if (!boardApi.isInside(this._col, this._row)) return false;
        const conns = boardApi.getConnections(this._col, this._row);
        if (!conns) return false;
        if (!conns[this.spawnDir]) {
            console.warn(`[TrainSimTrain ${this.trainId}] spawn cell has no exit in dir ${this.spawnDir}`);
            return false;
        }
        this._setPathState(boardApi, this._col, this._row, OPPOSITE[this.spawnDir], this.spawnDir);
        this._progress = 0;
        return true;
    }

    _crash(reason = 'crash') {
        this._alive = false;
        this._crashReason = reason;
        return { alive: false, reachedGoal: false, crashReason: reason };
    }

    _setPathState(boardApi, col, row, entryDir, exitDir) {
        this._prevCol = col + DIR_DELTA[entryDir].dc;
        this._prevRow = row + DIR_DELTA[entryDir].dr;
        this._nextCol = col + DIR_DELTA[exitDir].dc;
        this._nextRow = row + DIR_DELTA[exitDir].dr;

        const la = this._computeLookahead(boardApi, this._nextCol, this._nextRow, OPPOSITE[exitDir], exitDir);
        this._lookaheadCol = la.col;
        this._lookaheadRow = la.row;
    }

    _computeLookahead(boardApi, col, row, entryDir, fallbackDir) {
        if (!boardApi.isInside(col, row) || boardApi.isGoalFor(col, row, this.trainId)) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }
        const conns = boardApi.getConnections(col, row);
        if (!conns || !conns[entryDir]) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }
        // Consult the switch's routing if one lives here so the
        // lookahead curve matches the actual path the train will take.
        let exitDir = -1;
        if (boardApi.getSwitchExit) {
            exitDir = boardApi.getSwitchExit(col, row, entryDir);
        }
        if (exitDir === -1) exitDir = getExitDir(conns, entryDir);
        if (exitDir === -1) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }
        return {
            col: col + DIR_DELTA[exitDir].dc,
            row: row + DIR_DELTA[exitDir].dr,
        };
    }

    _sampleSegment(t) {
        const cs = this._cellSize;
        const p0 = this._cellCentre(this._prevCol, this._prevRow, cs);
        const p1 = this._cellCentre(this._col, this._row, cs);
        const p2 = this._cellCentre(this._nextCol, this._nextRow, cs);
        const p3 = this._cellCentre(this._lookaheadCol, this._lookaheadRow, cs);
        return {
            x: this._catmull(p0.x, p1.x, p2.x, p3.x, t),
            y: this._catmull(p0.y, p1.y, p2.y, p3.y, t),
        };
    }

    _cellCentre(col, row, cs) {
        return { x: col * cs + cs / 2, y: row * cs + cs / 2 };
    }

    _catmull(p0, p1, p2, p3, t) {
        const t2 = t * t;
        const t3 = t2 * t;
        return 0.5 * (
            (2 * p1) +
            (-p0 + p2) * t +
            (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
            (-p0 + 3 * p1 - 3 * p2 + p3) * t3
        );
    }

    _pushTrailPoint(point) {
        const last = this._trail[0];
        if (!last) { this._trail.unshift(point); return; }
        const dx = point.x - last.x;
        const dy = point.y - last.y;
        // Drop tiny sub-pixel motion (squared distance < 1 = movement
        // < 1 px). Used to be < 4 (i.e. <2 px gate), but on tight
        // corners that left only ~3-4 trail samples per cell of
        // arc — too sparse for the articulated coupling polyline to
        // bend smoothly through a 90° turn. Tighter gate doubles
        // the sample density for ~free; max length below caps total
        // memory.
        if ((dx * dx + dy * dy) < 1) return;
        this._trail.unshift(point);
        // 240 samples × ~1 px each = ~240 px = 8 cells of recorded
        // path, which comfortably covers head-to-tail (~2.3 cells)
        // plus several cells of look-back for the polyline window.
        if (this._trail.length > 240) this._trail.length = 240;
    }

    _sampleTrail(distanceBack) {
        if (this._trail.length === 0) return { x: 0, y: 0 };
        if (distanceBack <= 0) return this._trail[0];

        let remaining = distanceBack;
        for (let i = 0; i < this._trail.length - 1; i++) {
            const a = this._trail[i];
            const b = this._trail[i + 1];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.hypot(dx, dy);
            if (dist <= 0.001) continue;
            if (remaining <= dist) {
                const t = remaining / dist;
                return { x: a.x + dx * t, y: a.y + dy * t };
            }
            remaining -= dist;
        }
        return this._trail[this._trail.length - 1];
    }

    _drawTramCar(target, slotKey) {
        const cs = this._cellSize;
        // Trains are the star of the show — render them HUGE so the
        // tram-long PNGs read like proper locomotives, dominating
        // the board's road traffic at every level crossing.
        // ~2.3 cells long × 1.10 cells tall — sized so the locomotive
        // sits proportionally on the widened track instead of dwarfing
        // it (the earlier 3.4 × 1.65 made the tram ~4.7x taller than
        // the rail bed). Keep `_render`'s carSpacing + coupling offsets
        // in lockstep when editing these numbers, or the head + tail
        // will overlap or float apart.
        const w = Math.round(cs * 2.3);
        const h = Math.round(cs * 1.1);

        target.clear();
        target.removeChildren();

        const sprite = this._themeManager?.createSprite?.(slotKey, w, h);
        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            // No tint — different colour variants will arrive as
            // separate PNGs (one per train id), so leaving the
            // sprite untinted lets each train show its native paint.
            target.addChild(sprite);
            return;
        }

        drawTramCarRect(target, { x: -w / 2, y: -h / 2, w, h }, {
            horizontal: true,
            forwardSign: 1,
            windows: 3,
            bodyColor: this._color,
        });
    }

    /**
     * Build the small "→ <city>" tag that floats above the head
     * wagon during play. Created once at construction; the per-frame
     * `_render` step only updates its (x,y). Returns a Container.
     *
     * Visual style mirrors the gameplay station roof card so the
     * chip reads as part of the same UI language: bold white text
     * on a dark pill, train-coloured stroke, drop-shadow stroke on
     * the type for legibility on busy backgrounds (parks, roads,
     * other trains).
     */
    _buildDestChip(city) {
        const cs = this._cellSize;
        const wrap = new Container();

        const text = new Text({
            text: '→ ' + city,
            style: new TextStyle({
                fontFamily: 'Arial Black, Arial, sans-serif',
                fontSize: Math.max(11, Math.round(cs * 0.34)),
                fontWeight: '900',
                fill: '#ffffff',
                stroke: { color: 0x0b1220, width: Math.max(2, cs * 0.06) },
                letterSpacing: 0.8,
            }),
        });
        text.anchor.set(0.5);

        const padX = Math.max(6, cs * 0.22);
        const padY = Math.max(2, cs * 0.08);
        const w = text.width + padX * 2;
        const h = text.height + padY * 2;
        const radius = Math.max(5, cs * 0.18);

        const card = new Graphics();
        card.roundRect(-w / 2, -h / 2, w, h, radius);
        card.fill({ color: 0x0b1220, alpha: 0.88 });
        card.roundRect(-w / 2, -h / 2, w, h, radius);
        card.stroke({ color: this._color, width: Math.max(2, cs * 0.06) });
        // Inner highlight ring — same trick used on the station card
        // so the chip reads as glossy/UI rather than flat.
        card.roundRect(-w / 2 + 3, -h / 2 + 3, w - 6, h - 6, Math.max(3, radius - 3));
        card.stroke({ color: 0xffffff, width: 1, alpha: 0.18 });

        // Tiny notch on the bottom centre — points at the loco below
        // so the chip visually "belongs" to its train at a glance.
        const notchW = Math.max(4, cs * 0.18);
        const notchH = Math.max(3, cs * 0.12);
        card.moveTo(-notchW / 2, h / 2);
        card.lineTo( notchW / 2, h / 2);
        card.lineTo( 0,          h / 2 + notchH);
        card.closePath();
        card.fill({ color: 0x0b1220, alpha: 0.88 });
        card.stroke({ color: this._color, width: Math.max(1, cs * 0.04) });

        wrap.addChild(card);
        wrap.addChild(text);
        return wrap;
    }
}
